import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ApolloError, useMutation } from '@apollo/client';
import { LINKEDIN_INVITATION_NOTE_MAX_LENGTH, LinkedInApiProduct, Role } from '@bloomays-lib/types.shared';
import { SEND_LINKEDIN_INVITATION, SEND_LINKEDIN_MESSAGE } from '@bloomays-lib/adapter.api-talent';
import {
  AutoComplete,
  AutocompleteOption,
  Button,
  DrawerV2,
  LoaderPending,
  TabPanel,
  Tabs,
  TypeInput,
  ValidationTextField,
} from '@bloomays-lib/ui.shared';
import { useAuthStore } from '../../stores';
import { PageHeader } from '../molecules/PageHeader';
import { errorLogger } from '../../helpers/error';
import { notify } from '../../helpers/toastify';

type SendLinkedInMessageData = {
  sendLinkedInMessage: { chatId: string; messageId: string };
};

type SendLinkedInInvitationData = {
  sendLinkedInInvitation: { invitationId: string };
};

type OutreachAction = 'message' | 'invitation';

type HistoryEntry = {
  at: string;
  action: OutreachAction;
  variables: Record<string, unknown>;
  outcome:
    | { status: 'success'; response: unknown }
    | { status: 'error'; message: string; graphQLErrors: { message: string; extensions?: unknown }[] };
};

type UnipileAccountOption = AutocompleteOption & { linkedinApi: LinkedInApiProduct };

// Comptes Unipile de test configurés pour ce POC (TEC-3302) — pas encore persistés par Bloomie.
const UNIPILE_ACCOUNT_OPTIONS: UnipileAccountOption[] = [
  { id: 'GOVKaPDVSoS2wSbzKoyoUg', name: 'Joan', linkedinApi: LinkedInApiProduct.classic },
  { id: 'HeEWtvcvTYyFu10D8in6fw', name: 'Rodolphe', linkedinApi: LinkedInApiProduct.recruiter },
  { id: '9ZN6twGeQYqUZ-dNmktubg', name: 'Rosa', linkedinApi: LinkedInApiProduct.sales_navigator },
];

const toErrorOutcome = (error: ApolloError): HistoryEntry['outcome'] => ({
  status: 'error',
  message: error.message,
  graphQLErrors: error.graphQLErrors.map(({ message, extensions }) => ({ message, extensions })),
});

/**
 * TEC-3302 — Formulaires internes pour tester manuellement les deux envois LinkedIn
 * (POC Unipile). Réservé au rôle System.
 */
const PocUnipile = () => {
  const { t } = useTranslation(['pocUnipile']);
  const auth = useAuthStore((state) => state.auth);
  const isSystem = auth?.user?.roles?.includes(Role.System) || false;

  const [tab, setTab] = useState(0);
  const [account, setAccount] = useState<UnipileAccountOption | undefined>(undefined);
  const [talentLinkedInSlug, setTalentLinkedInSlug] = useState('');
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [messageResult, setMessageResult] = useState<SendLinkedInMessageData['sendLinkedInMessage'] | null>(null);
  const [invitationResult, setInvitationResult] = useState<SendLinkedInInvitationData['sendLinkedInInvitation'] | null>(
    null,
  );
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const record = (entry: Omit<HistoryEntry, 'at'>) =>
    setHistory((previous) => [{ at: new Date().toISOString(), ...entry }, ...previous]);

  const messageVariables = {
    accountId: account?.id ?? '',
    talentLinkedInSlug,
    text,
    linkedinApi: account?.linkedinApi,
  };
  const invitationVariables = { accountId: account?.id ?? '', talentLinkedInSlug, note };

  const [sendLinkedInMessage, { loading: sendingMessage }] = useMutation<SendLinkedInMessageData>(
    SEND_LINKEDIN_MESSAGE,
    {
      context: { clientName: 'api.talents' },
      onCompleted: (data) => {
        setMessageResult(data.sendLinkedInMessage);
        record({ action: 'message', variables: messageVariables, outcome: { status: 'success', response: data } });
        notify('success', t('sendMessageSuccess'), null, { hideProgressBar: false, autoClose: 3000 });
      },
      onError: (mutationError) => {
        setMessageResult(null);
        record({ action: 'message', variables: messageVariables, outcome: toErrorOutcome(mutationError) });
        errorLogger(mutationError, { extraInfos: 'sendLinkedInMessage failed in PocUnipile' });
        notify('error', mutationError.message, mutationError, { hideProgressBar: true, autoClose: 5000 });
      },
    },
  );

  const [sendLinkedInInvitation, { loading: sendingInvitation }] = useMutation<SendLinkedInInvitationData>(
    SEND_LINKEDIN_INVITATION,
    {
      context: { clientName: 'api.talents' },
      onCompleted: (data) => {
        setInvitationResult(data.sendLinkedInInvitation);
        record({
          action: 'invitation',
          variables: invitationVariables,
          outcome: { status: 'success', response: data },
        });
        notify('success', t('sendInvitationSuccess'), null, { hideProgressBar: false, autoClose: 3000 });
      },
      onError: (mutationError) => {
        setInvitationResult(null);
        record({ action: 'invitation', variables: invitationVariables, outcome: toErrorOutcome(mutationError) });
        errorLogger(mutationError, { extraInfos: 'sendLinkedInInvitation failed in PocUnipile' });
        notify('error', mutationError.message, mutationError, { hideProgressBar: true, autoClose: 5000 });
      },
    },
  );

  if (!isSystem) {
    return <Navigate to="/forbidden/norole" replace />;
  }

  const hasTarget = account !== undefined && talentLinkedInSlug.trim() !== '';

  const handleCopyHistory = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(history, null, 2));
      notify('success', t('historyCopied'), null, { hideProgressBar: false, autoClose: 2000 });
    } catch {
      notify('error', t('historyCopyFailed'), null, { hideProgressBar: true, autoClose: 4000 });
    }
  };

  const handleSubmitMessage = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!hasTarget || text.trim() === '') return;
    void sendLinkedInMessage({ variables: messageVariables });
  };

  const handleSubmitInvitation = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!hasTarget || note.trim() === '') return;
    void sendLinkedInInvitation({ variables: invitationVariables });
  };

  return (
    <Container>
      <HeaderRow>
        <PageHeader id="titlePocUnipile" title={t('title')} subtitle={t('subtitle')} />
        <Button
          textButton={t('historyButton', { count: history.length })}
          color="secondary"
          onClick={() => setHistoryOpen(true)}
        />
      </HeaderRow>

      <SectionTitle>{t('targetSectionTitle')}</SectionTitle>
      <TargetFields>
        <AutoComplete<UnipileAccountOption>
          data={UNIPILE_ACCOUNT_OPTIONS}
          label={t('accountIdLabel')}
          value={account}
          setValue={setAccount}
        />
        <ValidationTextField
          name="talentLinkedInSlug"
          id="talentLinkedInSlug"
          label={t('talentLinkedInSlugLabel')}
          value={talentLinkedInSlug}
          type={TypeInput.text}
          size="small"
          required
          onChange={(e) => setTalentLinkedInSlug(e.target.value)}
        />
      </TargetFields>

      <Tabs variant="menu" titles={[t('messageTabTitle'), t('invitationTabTitle')]} value={tab} setValue={setTab}>
        <TabPanel value={tab} index={0}>
          <SectionHelp>{t('messageFormHelp')}</SectionHelp>
          {sendingMessage && <LoaderPending text={t('messageSendingLoader')} />}
          <Form onSubmit={handleSubmitMessage}>
            <ValidationTextField
              name="messageText"
              id="messageText"
              label={t('messageTextLabel')}
              value={text}
              type={TypeInput.text}
              size="small"
              multiline
              minRows={3}
              required
              disabled={sendingMessage}
              onChange={(e) => setText(e.target.value)}
            />
            <ButtonsContainer>
              <Button
                textButton={t('messageSubmitButton')}
                buttonType="submit"
                disable={sendingMessage || !hasTarget || text.trim() === ''}
                loading={sendingMessage}
              />
            </ButtonsContainer>
          </Form>
          {messageResult && (
            <ResultBlock>
              <ResultTitle>{t('resultMessageTitle')}</ResultTitle>
              <div>chatId : {messageResult.chatId}</div>
              <div>messageId : {messageResult.messageId}</div>
            </ResultBlock>
          )}
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <SectionHelp>{t('invitationFormHelp')}</SectionHelp>
          {sendingInvitation && <LoaderPending text={t('invitationSendingLoader')} />}
          <Form onSubmit={handleSubmitInvitation}>
            <ValidationTextField
              name="invitationNote"
              id="invitationNote"
              label={t('invitationNoteLabel')}
              value={note}
              type={TypeInput.text}
              size="small"
              multiline
              minRows={3}
              required
              disabled={sendingInvitation}
              onChange={(e) => setNote(e.target.value)}
            />
            {/* Compteur informatif seulement : ne pas bloquer l'envoi, le refus au-delà de la limite est un cas à tester. */}
            <NoteCounter $exceeded={note.length > LINKEDIN_INVITATION_NOTE_MAX_LENGTH}>
              {t('invitationNoteCounter', { count: note.length, max: LINKEDIN_INVITATION_NOTE_MAX_LENGTH })}
              {note.length > LINKEDIN_INVITATION_NOTE_MAX_LENGTH &&
                ` — ${t('invitationNoteTooLongHint', { max: LINKEDIN_INVITATION_NOTE_MAX_LENGTH })}`}
            </NoteCounter>
            <ButtonsContainer>
              <Button
                textButton={t('invitationSubmitButton')}
                buttonType="submit"
                disable={sendingInvitation || !hasTarget || note.trim() === ''}
                loading={sendingInvitation}
              />
            </ButtonsContainer>
          </Form>
          {invitationResult && (
            <ResultBlock>
              <ResultTitle>{t('resultInvitationTitle')}</ResultTitle>
              <div>invitationId : {invitationResult.invitationId}</div>
            </ResultBlock>
          )}
        </TabPanel>
      </Tabs>

      <DrawerV2
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={t('historyTitle')}
        subtitle={t('historySubtitle')}
        width={560}
        footer={<Button textButton={t('historyCopyAll')} disable={history.length === 0} onClick={handleCopyHistory} />}
      >
        {history.length === 0 ? (
          <EmptyHistory>{t('historyEmpty')}</EmptyHistory>
        ) : (
          history.map((entry) => (
            <HistoryCard key={entry.at} $failed={entry.outcome.status === 'error'}>
              <HistoryHead>
                <strong>
                  {entry.action === 'invitation' ? t('historyActionInvitation') : t('historyActionMessage')}
                </strong>
                <time>{new Date(entry.at).toLocaleTimeString('fr-FR')}</time>
              </HistoryHead>
              <HistoryLabel>{t('historyRequestLabel')}</HistoryLabel>
              <Payload>{JSON.stringify(entry.variables, null, 2)}</Payload>
              <HistoryLabel>
                {entry.outcome.status === 'success' ? t('historyResponseLabel') : t('historyErrorLabel')}
              </HistoryLabel>
              <Payload>
                {entry.outcome.status === 'success'
                  ? JSON.stringify(entry.outcome.response, null, 2)
                  : JSON.stringify(
                      { message: entry.outcome.message, graphQLErrors: entry.outcome.graphQLErrors },
                      null,
                      2,
                    )}
              </Payload>
            </HistoryCard>
          ))
        )}
      </DrawerV2>
    </Container>
  );
};

export default PocUnipile;

const Container = styled('div')(() => ({
  margin: 'auto',
  width: '90%',
  maxWidth: 620,
}));

const HeaderRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',
}));

const SectionTitle = styled('h2')(() => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '16px',
  marginBottom: '8px',
}));

const SectionHelp = styled('p')(({ theme }) => ({
  color: theme.palette.neutral[60],
  fontSize: '14px',
  marginTop: 0,
  marginBottom: '16px',
}));

const TargetFields = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '8px',
}));

const Form = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const NoteCounter = styled('div', { shouldForwardProp: (prop) => prop !== '$exceeded' })<{ $exceeded: boolean }>(
  ({ theme, $exceeded }) => ({
    alignSelf: 'flex-end',
    fontSize: '12px',
    marginTop: '4px',
    color: $exceeded ? theme.palette.error.main : theme.palette.neutral[60],
  }),
);

const ButtonsContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '16px',
}));

const ResultBlock = styled('div')(({ theme }) => ({
  marginTop: '24px',
  padding: '16px',
  borderRadius: '12px',
  border: `1px solid ${theme.palette.neutral[20]}`,
  backgroundColor: theme.palette.neutral.white,
}));

const ResultTitle = styled('div')(() => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  marginBottom: '8px',
}));

const EmptyHistory = styled('p')(({ theme }) => ({
  color: theme.palette.neutral[60],
  fontSize: '14px',
}));

const HistoryCard = styled('div', { shouldForwardProp: (prop) => prop !== '$failed' })<{ $failed: boolean }>(
  ({ theme, $failed }) => ({
    marginBottom: '16px',
    padding: '12px',
    borderRadius: '10px',
    border: `1px solid ${$failed ? theme.palette.error.main : theme.palette.neutral[20]}`,
  }),
);

const HistoryHead = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  fontSize: '14px',
  marginBottom: '8px',
  color: theme.palette.neutral[60],
}));

const HistoryLabel = styled('div')(({ theme }) => ({
  color: theme.palette.neutral[60],
  fontSize: '12px',
  marginTop: '8px',
}));

const Payload = styled('pre')(({ theme }) => ({
  margin: '4px 0 0',
  padding: '8px',
  borderRadius: '8px',
  backgroundColor: theme.palette.neutral[10],
  fontSize: '12px',
  overflowX: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}));
